import { AltiusRes, UrlQueryParams } from "store/types";

import { toFormData } from "../../../utils";
import {
  AccountSignUpData,
  AuthSuccessPayload,
  AuthSuccessResponse,
  IAuthData,
  ICheckInviteTokenResponse,
  // AuthSuccess,
  IEasyLoginResponse,
  SessionSuccess
} from "../../types/auth";
import { AuthAction } from "../actions";
import { AsyncActions, AsyncActionsParams } from "../base";

export interface IAuthActions {
  checkDomainAction: (domain: string) => Promise<void>;
  checkInviteTokenAction: (token: string) => Promise<ICheckInviteTokenResponse>;
  signUpAction: (data: {
    first_name: string;
    last_name: string;
    company_name: string;
    phone_number: string;
    email: string;
    password: string;
    photo: File;
    invite?: string;
  }) => Promise<void>;
  authAction: (data: IAuthData) => Promise<AuthSuccessResponse>;
  forgotPasswordAction: (email: string) => Promise<void>;
  resetPasswordAction: (data: {
    token: string;
    email: string;
    password: string;
    password_confirmation: string;
  }) => Promise<void>;
  easyLoginAction: (email: string) => Promise<IEasyLoginResponse>;
  removeTokenAction: () => void;
  logoutAction: () => Promise<void>;
  sessionAction: (queryKeys: Array<keyof UrlQueryParams>) => Promise<void>;
  loginWithAzureAction: (token: string) => Promise<void>;
  signUpAccountAction: (data: AccountSignUpData) => Promise<void>;
  inviteFriendsAction: (data: { message: string; emails: string[] }) => Promise<void>;
  setAuthCookieAction: (token: string) => Promise<void>;
  getAccountSignDataAction: (token: string) => Promise<Record<string, string>>;
  checkSSOAction: () => Promise<{
    saml_sso_enabled: boolean;
    azure_sso_enabled: boolean;
    sso_app_id: string | null;
    sso_app_authority: string | null;
    sso_only: boolean;
  }>;
  getReportGeneratorHashAction: () => Promise<string>;
}
export class AuthActions extends AsyncActions implements IAuthActions {
  constructor(params: AsyncActionsParams) {
    super(params);
  }

  public checkDomainAction: IAuthActions["checkDomainAction"] = async (domain) => {
    await this.api.post({
      url: "/api/v0.0.2/check_domain",
      data: toFormData({ domain: domain })
    });
  };

  public checkInviteTokenAction: IAuthActions["checkInviteTokenAction"] = async (token) => {
    const { data: response } = await this.api.get<AltiusRes<ICheckInviteTokenResponse>>({
      url: "/api/v0.0.2/invites/check",
      params: { invite_token: token }
    });
    return response.data;
  };

  public signUpAction: IAuthActions["signUpAction"] = async (data) => {
    await this.api.post({ url: "/api/v0.0.2/signup", data });
  };

  public authAction: IAuthActions["authAction"] = async (data) => {
    const { data: response } = await this.api.post<
      { success: { redirect_to: `${string}.${string}.${string}` } } | { verification_code: boolean }
    >({
      url: "/api/v0.0.2/login",
      data
    });

    const result: AuthSuccessResponse = {};

    const redirect_to = (response as { success: { redirect_to: `${string}.${string}.${string}` } })?.success
      ?.redirect_to;
    const verification_code = (response as { verification_code: boolean })?.verification_code;

    if (redirect_to) {
      result.redirect_to = redirect_to;
    }

    if (verification_code) {
      result.verification_code = verification_code;
    }

    return result;
  };

  public forgotPasswordAction: IAuthActions["forgotPasswordAction"] = async (email) => {
    await this.api.post({
      url: "/api/v0.0.2/reset-password/email",
      data: { email }
    });
  };

  public resetPasswordAction: IAuthActions["resetPasswordAction"] = async (data) => {
    await this.api.post({ url: "/api/v0.0.2/reset-password/reset", data });
  };

  public easyLoginAction: IAuthActions["easyLoginAction"] = async (email: string) => {
    const {
      data: { data: response }
    } = await this.api.post<AltiusRes<IEasyLoginResponse>>({
      url: "/api/v0.0.3/login-code",
      data: { email }
    });
    return response;
  };

  public removeTokenAction: IAuthActions["removeTokenAction"] = () => {
    this.dispatch({ type: AuthAction.RemoveToken, payload: null });
  };

  public logoutAction: IAuthActions["logoutAction"] = async () => {
    await this.api.post({ url: "/api/v0.0.2/logout" });
    this.dispatch({ type: AuthAction.Logout, payload: null });
  };

  public sessionAction: IAuthActions["sessionAction"] = async (queryKeys) => {
    const {
      data: { data: payload }
    } = await this.api.get<AltiusRes<SessionSuccess>>({ url: "/api/v0.0.2/users/session" });
    this.dispatch({
      type: AuthAction.SessionSuccess,
      payload: { ...payload, urlQueryKeys: queryKeys } as AuthSuccessPayload
    });
  };

  public loginWithAzureAction: IAuthActions["loginWithAzureAction"] = async (token) => {
    await this.api.post({
      url: "/api/v0.0.2/azure-login",
      data: { access_token: token }
    });
  };

  public signUpAccountAction: IAuthActions["signUpAccountAction"] = async (data) => {
    await this.api.post({ url: "/api/v0.0.3/account-sign-up", data });
  };

  public inviteFriendsAction: IAuthActions["inviteFriendsAction"] = async (data) => {
    await this.api.post({ url: "/api/v0.0.3/profile/invite-friends", data });
  };

  public setAuthCookieAction: IAuthActions["setAuthCookieAction"] = async (token) => {
    await this.api.post({ url: "/api/v0.0.3/auto-login", data: { token } });
  };

  public getAccountSignDataAction: IAuthActions["getAccountSignDataAction"] = async (token) => {
    const {
      data: { data }
    } = await this.api.get<AltiusRes<Record<string, string>>>({
      url: "/api/v0.0.3/pending-account",
      params: { token }
    });
    return data;
  };

  public checkSSOAction: IAuthActions["checkSSOAction"] = async () => {
    const {
      data: { data }
    } = await this.api.get<
      AltiusRes<{
        saml_sso_enabled: boolean;
        azure_sso_enabled: boolean;
        sso_app_id: string | null;
        sso_app_authority: string | null;
        sso_only: boolean;
      }>
    >({
      url: "/api/v0.0.2/check-sso"
    });
    return data;
  };

  public getReportGeneratorHashAction: IAuthActions["getReportGeneratorHashAction"] = async () => {
    const {
      data: { data }
    } = await this.api.get<AltiusRes<{ uuid: string}>>({
      url: "/api/v0.0.3/user/data-extractor-uuid"
    });
    return data.uuid;
  };
}
